import { AUTH_TOKEN_KEY } from "@core/account/utils";
import { parseError, URLS } from "@core/shared/utils";
import { errorToast, toastConfirm } from "@design-system/components/toast";
import { useAction } from "@framework/shared/hooks";
import cache from "@mongez/cache";
import { trans } from "@mongez/localization";
import { type CredentialResponse } from "@react-oauth/google";
import { useNavigate } from "@remix-run/react";
import { User } from "../../../../core/account/types";
import { userAtom } from "../../config/atoms";
import {
  loginBrowserHelpAtom,
  resetPasswordAtom,
  verifyRegisterAtom,
} from "../atoms";
import {
  onSuccessForgetPassword,
  onSuccessLogin,
  onSuccessRegister,
  onSuccessResetPassword,
  onSuccessVerifyForgetPassword,
  onSuccessVerifyRegister,
} from "../events";
import type { LoginDiagnosticStage } from "../login-diagnostics";
import {
  getSafeLoginErrorDiagnostic,
  logLoginDiagnostic,
} from "../login-diagnostics";
import type { LoginFailureContext } from "../login-failure-alert";
import { resolveLoginFailureAlert } from "../login-failure-alert";
import {
  getTrustedDeviceBrowserHelp,
  getTrustedDevicePreparationStage,
  isTrustedDeviceTimeoutError,
} from "../trusted-device-preparation";
import { useTrustedDeviceLogin } from "./use-trusted-device-login.hook";

export * from "./use-create-account-hooks";
export * from "./use-trusted-device-login.hook";

/**
 * Show the one alert a student can act on for a diagnosed login failure.
 *
 * A vetted backend rejection is shown as-is; every other cause resolves to a
 * localized key so no caught exception text ever reaches the toast.
 */
function showLoginFailureAlert(context: LoginFailureContext): void {
  const alert = resolveLoginFailureAlert(context);

  loginBrowserHelpAtom.update({ reason: context.browserHelp ?? null });

  errorToast(alert.serverMessage || trans(alert.translationKey));
}

/**
 * Report and show a trusted-device preparation failure that happened before
 * a register/verify request was even sent.
 *
 * Returns `true` when the error was a preparation failure and was handled.
 */
function handleTrustedDevicePreparationFailure(
  event: string,
  error: unknown,
): boolean {
  const stage = getTrustedDevicePreparationStage(error);

  if (!stage) return false;

  logLoginDiagnostic(
    event,
    { stage, ...getSafeLoginErrorDiagnostic(error) },
    "error",
  );

  showLoginFailureAlert({
    stage,
    timedOut: isTrustedDeviceTimeoutError(error),
    browserHelp: getTrustedDeviceBrowserHelp(error),
  });

  return true;
}

/**
 * Login hook
 * It return the onSubmit callback
 */
export function useLogin() {
  const submit = useAction("/auth/login");
  const trustedDeviceLogin = useTrustedDeviceLogin();

  const login = async ({ values, form }) => {
    let stage: LoginDiagnosticStage = "loginAction";

    try {
      Object.assign(values, await trustedDeviceLogin.prepareLoginDevice());

      const value = await submit(values);

      if (!value.isOK) {
        logLoginDiagnostic("login_action_rejected", {
          stage,
          responseStatus: value.status,
          hasFieldErrors: Boolean(value.errors?.length),
        });

        showLoginFailureAlert({
          stage,
          responseStatus: value.status,
          serverMessage: value.error,
        });

        return;
      }

      stage = "loginCompletion";

      onSuccessLogin(value.data.user);
    } catch (error) {
      const failedStage = getTrustedDevicePreparationStage(error) ?? stage;
      const diagnostic = getSafeLoginErrorDiagnostic(error);

      logLoginDiagnostic(
        "login_failed",
        {
          stage: failedStage,
          ...diagnostic,
        },
        "error",
      );

      showLoginFailureAlert({
        stage: failedStage,
        timedOut: isTrustedDeviceTimeoutError(error),
        browserHelp: getTrustedDeviceBrowserHelp(error),
      });
    } finally {
      form.submitting(false);
    }
  };

  return login;
}

/**
 * Authenticate the current student directly from their scanned card.
 */
export function useQrLogin() {
  const submit = useAction("/auth/login/qr");
  const trustedDeviceLogin = useTrustedDeviceLogin();

  return async (code: string) => {
    let stage: LoginDiagnosticStage = "loginAction";

    try {
      const trustedDevice = await trustedDeviceLogin.prepareLoginDevice();
      const value = await submit({
        code,
        ...trustedDevice,
      });

      if (!value.isOK) {
        logLoginDiagnostic("qr_login_action_rejected", {
          stage,
          responseStatus: value.status,
        });

        showLoginFailureAlert({
          stage,
          responseStatus: value.status,
          serverMessage: value.error,
        });

        return false;
      }

      stage = "loginCompletion";

      onSuccessLogin(value.data.user);

      return true;
    } catch (error) {
      const failedStage = getTrustedDevicePreparationStage(error) ?? stage;
      const diagnostic = getSafeLoginErrorDiagnostic(error);

      logLoginDiagnostic(
        "qr_login_failed",
        {
          stage: failedStage,
          ...diagnostic,
        },
        "error",
      );

      showLoginFailureAlert({
        stage: failedStage,
        timedOut: isTrustedDeviceTimeoutError(error),
        browserHelp: getTrustedDeviceBrowserHelp(error),
      });

      return false;
    }
  };
}

type UseCreateAccountOptions = {
  onSuccess?: () => void;
};

/**
 * Create account/Register hook
 * It return the onSubmit callback
 */
export function useCreateAccount(options: UseCreateAccountOptions = {}) {
  const navigate = useNavigate();
  const submit = useAction("/auth/register");
  const trustedDeviceLogin = useTrustedDeviceLogin();

  const register = async ({ values, form }) => {
    try {
      Object.assign(values, await trustedDeviceLogin.prepareLoginDevice());
    } catch (error) {
      form.submitting(false);

      if (handleTrustedDevicePreparationFailure("register_device_failed", error)) {
        return;
      }

      throw error;
    }

    const { error, errors, data } = await submit(values);

    if (errors) {
      for (const error of errors) {
        form.control(error.key)?.setError(error.error);
      }

      form.submitting(false);

      return;
    } else if (error) {
      form.submitting(false);

      return errorToast(error);
    }

    options.onSuccess?.();

    if (data?.activationUsing) {
      verifyRegisterAtom.merge({
        verificationType: data.activationUsing,
        email: values.email,
        phoneNumber: values.phoneNumber,
      });

      return navigate(URLS.auth.verifyCode);
    }

    onSuccessRegister(navigate, data.user);
  };

  return register;
}

/**
 * Verify register code hook
 * Use this hook to verify user account after registration
 */
export function useRegisterVerifyCode() {
  const navigate = useNavigate();
  const submit = useAction("/auth/verify-register");
  const trustedDeviceLogin = useTrustedDeviceLogin();

  const verifyCodeSubmit = async ({ values, form }) => {
    let loginDevice: Awaited<
      ReturnType<typeof trustedDeviceLogin.prepareLoginDevice>
    >;

    try {
      loginDevice = await trustedDeviceLogin.prepareLoginDevice();
    } catch (error) {
      form.submitting(false);

      if (
        handleTrustedDevicePreparationFailure(
          "verify_register_device_failed",
          error,
        )
      ) {
        return;
      }

      throw error;
    }

    const { error, errors, data } = await submit({
      ...values,
      ...loginDevice,
    });

    form.submitting(false);

    if (errors) {
      for (const error of errors) {
        form.control(error.key)?.setError(error.error);
      }

      return;
    } else if (error) {
      form.submitting(false);

      return errorToast(error);
    }

    onSuccessVerifyRegister(navigate, data.user);
  };

  return verifyCodeSubmit;
}

/**
 * Perform logout
 */
export function useLogout() {
  const logoutAction = useAction("/auth/logout");
  const navigate = useNavigate();

  return async () => {
    try {
      const confirmed = await toastConfirm(
        trans("logout"),
        trans("auth.confirmLogOut"),
      );

      if (!confirmed) return;

      await logoutAction();

      userAtom.update({} as User);

      // Clear backup token from localStorage
      cache.remove(AUTH_TOKEN_KEY);

      // Refresh current route
      navigate(0);
    } catch (error) {
      errorToast(parseError(error));
    }
  };
}

/**
 * Send forget password request hook
 */
export function useForgetPassword() {
  const navigate = useNavigate();
  const submit = useAction("/auth/forgot-password");

  const forgetPasswordSubmit = async ({ values, form }) => {
    try {
      const value = await submit(values);

      localStorage.setItem("forgetPasswordEmail", values.email);
      resetPasswordAtom.merge({
        email: values.email,
      });

      form.submitting(false);

      if (value.error) {
        return errorToast(value.error);
      }

      onSuccessForgetPassword(navigate, values, form);
    } catch (error) {
      errorToast(parseError(error));
    }
  };

  return forgetPasswordSubmit;
}

/**
 * Verify forget password code hook
 */
export function useVerifyForgetPasswordOTP() {
  const navigate = useNavigate();
  const submit = useAction(URLS.auth.verifyCode);

  const verifyForgetPasswordOTPSubmit = async ({ values, form }) => {
    try {
      const storedEmail = resetPasswordAtom.value.email;
      const email =
        storedEmail || localStorage.getItem("forgetPasswordEmail") || "";

      if (!email) {
        return errorToast(trans("auth.emailNotFound"));
      }

      const value = await submit({
        ...values,
        email,
      });

      form.submitting(false);

      if (value.error) {
        return errorToast(value.error);
      }

      onSuccessVerifyForgetPassword(navigate, values);
    } catch (error) {
      errorToast(parseError(error));
    }
  };

  return verifyForgetPasswordOTPSubmit;
}

/**
 * Reset password hook
 */
export function useResetPassword() {
  const navigate = useNavigate();
  const submit = useAction("/auth/reset-password");

  const resetPasswordSubmit = async ({ values, form }) => {
    try {
      const { code: storedCode, email: storedEmail } = resetPasswordAtom.value;

      const code =
        storedCode || localStorage.getItem("forgetPasswordCode") || "";
      const email =
        storedEmail || localStorage.getItem("forgetPasswordEmail") || "";

      if (!email) {
        return errorToast(trans("auth.emailNotFound"));
      }

      const value = await submit({
        ...values,
        email,
        code,
      });

      form.submitting(false);
      if (value.error) {
        return errorToast(value.error);
      }

      onSuccessResetPassword(navigate);
    } catch (error) {
      errorToast(parseError(error));
    }
  };

  return resetPasswordSubmit;
}

export function useLoginWithGoogle() {
  const loginByGoogleAction = useAction("/auth/login/google");

  const handleGoogleLogin = async (credentialResponse: CredentialResponse) => {
    try {
      const value = await loginByGoogleAction({
        credential: credentialResponse.credential,
      });

      if (value.error) {
        return errorToast(value.error);
      }

      onSuccessLogin(value.data.user);
    } catch (error) {
      errorToast(parseError(error));
    }
  };

  return handleGoogleLogin;
}
